import React, { useState, useEffect } from 'react';
import {
  X,
  Building2,
  Upload,
  Save,
  Palette,
  Globe,
  Shield,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { getCompanyId, getCompanyDisplayName } from '../utils/tenantContext';

interface CompanyManagementProps {
  onClose: () => void;
  isSuperAdmin?: boolean; // Kept for prop compatibility but unused
}

export default function CompanyManagement({
  onClose,
}: CompanyManagementProps) {
  const companyId = getCompanyId();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: getCompanyDisplayName(),
    domain: companyId,
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    logo: '',
    email: 'admin@' + companyId + '.com',
    phone: '',
    address: '',
    website: ''
  });

  // Load saved settings from localStorage if available
  useEffect(() => {
    const savedSettings = localStorage.getItem(`company_settings_${companyId}`);
    if (savedSettings) {
      setFormData(JSON.parse(savedSettings));
    }
  }, [companyId]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setFormData((prev) => ({ ...prev, logo: event.target.result as string }));
        toast.success('Logo uploaded');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save to localStorage for persistence in this demo
      localStorage.setItem(`company_settings_${companyId}`, JSON.stringify(formData));
      
      toast.success('Company settings updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col animate-in fade-in duration-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              Company Settings
            </h2>
            <p className="text-sm text-blue-100 mt-1">
              Manage your organization profile and branding
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-50">
        <div className="max-w-3xl mx-auto space-y-6">
          
          {/* Branding Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-blue-600" />
                Branding & Identity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Company Logo
                </label>
                <div className="flex items-center space-x-6">
                  <div className="relative group">
                    {formData.logo ? (
                      <ImageWithFallback
                        src={formData.logo}
                        alt="Company Logo"
                        className="h-24 w-24 rounded-xl object-cover border-2 border-gray-200 shadow-sm"
                      />
                    ) : (
                      <div className="h-24 w-24 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                        <Building2 className="h-10 w-10 text-white" />
                      </div>
                    )}
                    <label className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg transform translate-x-1/4 translate-y-1/4">
                      <Upload className="h-4 w-4 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">Upload your logo</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Recommended size: 512x512px. Max file size: 2MB.
                      Supported formats: PNG, JPG, SVG.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Company Name */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Company Name
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="h-11"
                  />
                </div>

                {/* Domain (Read Only) */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Tenant Domain
                  </label>
                  <div className="flex items-center">
                    <div className="bg-gray-100 border border-r-0 border-gray-300 rounded-l-md px-3 h-11 flex items-center text-gray-500">
                      <Globe className="h-4 w-4" />
                    </div>
                    <Input
                      value={formData.domain}
                      readOnly
                      className="h-11 rounded-l-none bg-gray-50 text-gray-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Contact support to change your domain</p>
                </div>

                {/* Primary Color */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Primary Brand Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.primaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="h-11 w-16 rounded cursor-pointer border border-gray-300 p-1"
                    />
                    <Input
                      value={formData.primaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="h-11 font-mono uppercase"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Secondary Brand Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.secondaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className="h-11 w-16 rounded cursor-pointer border border-gray-300 p-1"
                    />
                    <Input
                      value={formData.secondaryColor}
                      onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className="h-11 font-mono uppercase"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Organization Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    Support Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="h-11"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    Support Phone
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="h-11"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    Headquarters Address
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="h-11"
                    placeholder="123 Oil Field Rd, Midland, TX 79701"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-400" />
                    Website URL
                  </label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    className="h-11"
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSave} 
              disabled={isLoading}
              className="h-12 px-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg"
            >
              {isLoading ? (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Save className="h-5 w-5 mr-2" />
              )}
              Save Changes
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
